import { useState } from "react"
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
} from "@mui/material"
import {
  Visibility as VisibilityIcon,
  Comment as CommentIcon,
} from "@mui/icons-material"
import { useApiQuery, useApiMutation } from "../../hooks/useApi"
import { useQueryClient } from "@tanstack/react-query"
import api from "../../utils/api"
import JobsTable from "../../components/jobs/JobsTable"
import JobDetailsDialog from "../../components/jobs/JobDetailsDialog"
import { useAuth } from "../../contexts/AuthContext"

function ProcessingJobsPage() {

  const auth = useAuth()

  const [selectedJob, setSelectedJob] = useState(null)
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [actionDialogOpen, setActionDialogOpen] = useState(false)
  const [dialogType, setDialogType] = useState(null) // "status" | "subStatus" | "comment"
  const [comments, setComments] = useState("")
  const [jobDetailsForView, setJobDetailsForView] = useState(null)

  const queryClient = useQueryClient()

  const { data: jobs, isLoading } = useApiQuery(
    ["secondary-jobs"],
    `/jobs/${auth?.user?._id}/assigned/secondary`
  )

  const setJobCommentMutation = useApiMutation(
    `/jobs/${selectedJob?.JobId}/comments`,
    "POST",
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: "processing-jobs" })
        handleCloseActionDialog()
      },
    }
  )

  // Define table columns
  const columns = [
    { id: "jobId", label: "Job ID", field: "JobId", type: "jobId" },
    { id: "client", label: "Client", field: "clientName" },
    { id: "jobType", label: "Job Type", field: "jobType" },
    { id: "dueDate", label: "Due Date", field: "dueDate", type: "date" },
    { id: "status", label: "Status", field: "status", type: "status" },
    { id: "subStatus", label: "Sub Status", field: "subStatus", type: "subStatus"},
  ]

  // Define actions menu items
  const actions = [
    {
      id: "view",
      label: "View Details",
      icon: <VisibilityIcon />,
    },
    {
      id: "addComment",
      label: "Add Comment",
      icon: <CommentIcon />,
    },
  ]

  const handleActionClick = async (action, job) => {
    setSelectedJob(job)

    switch (action.id) {
      case "view":
        // Fetch full job details
        try {
          const jobDetails = await api.get(`/jobs/${job.JobId}`)
          setJobDetailsForView(jobDetails.data[0])
          setViewDialogOpen(true)
        } catch (error) {
          console.error("Failed to fetch job details:", error)
        }
        break
      case "setStatus":
        setDialogType("status")
        setActionDialogOpen(true)
        break
      case "setSubStatus":
        setDialogType("subStatus")
        setActionDialogOpen(true)
        break
      case "addComment":
        setDialogType("comment")
        setActionDialogOpen(true)
        break
      default:
        break
    }
  }

  const handleCloseActionDialog = () => {
    setActionDialogOpen(false)
    setDialogType(null)
    setComments("")
    setSelectedJob(null)
  }

  const handleConfirmUpdate = () => {
    let data = {}

    if (dialogType === "comment") {
      data = {
        commentText: comments || "",
      }
      setJobCommentMutation.mutate(data)
    }
  }

  if (isLoading) {
    return <div>Loading....</div>
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        All Secondary Jobs
      </Typography>
      <Typography variant="body1" color="textSecondary" paragraph>
        These are your secondary jobs.
      </Typography>

      {/* Jobs Table with Reusable Component */}
      <JobsTable
        jobs={jobs || []}
        columns={columns}
        actions={actions}
        onActionClick={handleActionClick}
        emptyMessage="No jobs in this status"
      />

      {/* View Details Dialog - Reusable Component */}
      <JobDetailsDialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false)
          setJobDetailsForView(null)
        }}
        job={jobDetailsForView}
      />

      {/* Action Dialog (Status, SubStatus, Comment) */}
      <Dialog
        open={actionDialogOpen}
        onClose={handleCloseActionDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {dialogType === "status"
            ? "Update Job Status"
            : dialogType === "subStatus"
            ? "Update Job Sub Status"
            : "Add Comment"}
        </DialogTitle>
        <DialogContent>
          {selectedJob && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="h6" gutterBottom>
                {selectedJob.JobId} - {selectedJob.jobType}
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Client: {selectedJob.clientName}
              </Typography>
              {(
                dialogType === "comment") && (
                <TextField
                  fullWidth
                  label="Comments"
                  multiline
                  rows={3}
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder="Add notes about the update..."
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseActionDialog}>Cancel</Button>
          <Button
            onClick={handleConfirmUpdate}
            variant="contained"
            disabled={
              (dialogType === "comment" && !comments)
            }
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ProcessingJobsPage